import {create} from 'zustand';
import {PageResponse} from '@/types';
import {errorLog} from '@/utils';
import {
  getAgentRuleData,
  getLevelOneTeamReport,
  getRewardBetSummary,
  getTeamBetCommission,
  getTeamDepositBonus,
  getTeamDevelopedUser,
} from '../agent.service';
import {
  AgentDateRangeParams,
  AgentLevelOneTeamReport,
  AgentRewardBetSummary,
  AgentRuleData,
  AgentTeamBetCommissionItem,
  AgentTeamDepositBonusItem,
  AgentTeamDevelopedUserItem,
} from '../agent.type';

export type AgentReportFilter =
  | 'yesterday'
  | 'today'
  | 'threeDays'
  | 'week'
  | 'fifteenDays'
  | 'month';

interface LoadListParams extends AgentDateRangeParams {
  userId?: number;
}

interface AgentDataState {
  myDataLoading: boolean;
  subordinateLoading: boolean;
  commissionLoading: boolean;
  depositBonusLoading: boolean;
  ruleLoading: boolean;
  levelOneTeamReport?: AgentLevelOneTeamReport;
  rewardBetSummary?: AgentRewardBetSummary;
  subordinateList?: PageResponse<AgentTeamDevelopedUserItem>;
  commissionList?: PageResponse<AgentTeamBetCommissionItem>;
  depositBonusList?: PageResponse<AgentTeamDepositBonusItem>;
  ruleData?: AgentRuleData;
  loadMyData: (range: AgentDateRangeParams) => Promise<void>;
  loadSubordinateData: (params: LoadListParams) => Promise<void>;
  loadCommissionData: (params: LoadListParams) => Promise<void>;
  loadDepositBonusData: (params: LoadListParams) => Promise<void>;
  loadRuleData: () => Promise<void>;
}

const pad2 = (value: number) => `${value}`.padStart(2, '0');

export const toAgentDateKey = (date: Date) =>
  Number(
    `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`,
  );

const startOfDay = (date: Date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const endOfDay = (date: Date) => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

export const getAgentReportRange = (
  filter: AgentReportFilter = 'today',
): AgentDateRangeParams => {
  const now = new Date();
  const start = startOfDay(now);
  const end = endOfDay(now);

  if (filter === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  }

  if (filter === 'week') {
    start.setDate(start.getDate() - 6);
  }

  if (filter === 'threeDays') {
    start.setDate(start.getDate() - 2);
  }

  if (filter === 'fifteenDays') {
    start.setDate(start.getDate() - 14);
  }

  if (filter === 'month') {
    start.setDate(1);
  }

  return {
    startDate: toAgentDateKey(start),
    endDate: toAgentDateKey(end),
  };
};

export const getAgentDateRangeLabel = ({
  startDate,
  endDate,
}: AgentDateRangeParams) => {
  const format = (value?: number) => {
    const date = `${value || ''}`;
    if (date.length !== 8) {
      return '';
    }
    return `${date.slice(4, 6)}/${date.slice(6, 8)}/${date.slice(0, 4)}`;
  };

  const start = format(startDate);
  const end = format(endDate);
  return start && end ? `${start}-${end}` : '';
};

export const useAgentDataStore = create<AgentDataState>(set => ({
  myDataLoading: false,
  subordinateLoading: false,
  commissionLoading: false,
  depositBonusLoading: false,
  ruleLoading: false,
  levelOneTeamReport: undefined,
  rewardBetSummary: undefined,
  subordinateList: undefined,
  commissionList: undefined,
  depositBonusList: undefined,
  ruleData: undefined,

  loadMyData: async range => {
    set({myDataLoading: true});
    try {
      const [levelOneTeamReport, rewardBetSummary] = await Promise.all([
        getLevelOneTeamReport(range),
        getRewardBetSummary(range),
      ]);
      set({levelOneTeamReport, rewardBetSummary});
    } catch (error) {
      errorLog('agent my data load failed', error);
    } finally {
      set({myDataLoading: false});
    }
  },

  loadSubordinateData: async params => {
    set({subordinateLoading: true});
    try {
      const subordinateList = await getTeamDevelopedUser({
        ...params,
        pageNo: 1,
        pageSize: 20,
      });
      set({subordinateList});
    } catch (error) {
      errorLog('agent subordinate data load failed', error);
    } finally {
      set({subordinateLoading: false});
    }
  },

  loadCommissionData: async params => {
    set({commissionLoading: true});
    try {
      const commissionList = await getTeamBetCommission({
        ...params,
        pageNo: 1,
        pageSize: 20,
      });
      set({commissionList});
    } catch (error) {
      errorLog('agent commission data load failed', error);
    } finally {
      set({commissionLoading: false});
    }
  },

  loadDepositBonusData: async params => {
    set({depositBonusLoading: true});
    try {
      const depositBonusList = await getTeamDepositBonus({
        ...params,
        pageNo: 1,
        pageSize: 20,
      });
      set({depositBonusList});
    } catch (error) {
      errorLog('agent deposit bonus data load failed', error);
    } finally {
      set({depositBonusLoading: false});
    }
  },

  loadRuleData: async () => {
    set({ruleLoading: true});
    try {
      const ruleData = await getAgentRuleData();
      set({ruleData});
    } catch (error) {
      errorLog('agent rule data load failed', error);
    } finally {
      set({ruleLoading: false});
    }
  },
}));
